import React, { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import InView from 'react-intersection-observer';
import { useDebounceFn } from 'ahooks';

import { EventNameAMP } from 'application/services/AnalyticsService';
import { VISIBLE_TITLE_LENGTH, VISIBLE_TITLE_LENGTH_MOBILE } from 'application/components/CompanyTagChip';
import SuggestionUtils from 'application/utils/SuggestionUtils';
import {
  AUTOCOMPLETE_DROPDOWN_MENU_MARGIN,
  AUTOCOMPLETE_MAX_VISIBLE_OPTIONS,
} from 'application/components/common/CompanyTagsProvider/consts';
import { FN_OPTIONS } from 'application/consts';

import { DropdownItemDataType } from '@zvuk-b2b/react-uikit/ui/Dropdown';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import Spinner from '@zvuk-b2b/react-uikit/ui/Spinner';
import { Colors } from '@zvuk-b2b/react-uikit/ui/types';

import TagFiltersUtils from './utils';
import {
  DEFAULT_TAG_FILTERS_STATE,
  DESKTOP_PADDING,
  DROPDOWN_DESKTOP_BUTTON_WIDTH,
  DROPDOWN_MOBILE_BUTTON_WIDTH,
  DUMMY_COUNT,
  GRID_GAP,
  MOBILE_PADDING,
  TAG_REST_WIDTH,
} from './consts';
import { UseTagFiltersControllerProps } from './types';
import { tagFiltersReducer } from './reducer';

import styles from './index.module.scss';

const useTagFiltersController = ({
  companyId,
  companyTags,
  paginationInfo,
  requestCompanyTags,
  loadMoreCompanyTags,
  onFilterExecute,
  tagIds,
  pageType,
  rootRef,
  dropdownInTagsRow = false,
  onChange,
}: UseTagFiltersControllerProps) => {
  const { isDesktop } = useMedia();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>();

  const [state, dispatch] = useReducer(tagFiltersReducer, DEFAULT_TAG_FILTERS_STATE);

  const onExecute = async (searchQuery: string) => {
    await onFilterExecute({
      companyId,
      title: searchQuery,
    });
  };

  const { run: tagsRequest } = useDebounceFn((query: string) => onExecute(query), FN_OPTIONS);

  const onSearchChange = (value: string) => {
    dispatch({ type: 'setSearchQuery', searchQuery: value.length === 1 ? value.toUpperCase() : value });
    tagsRequest(value);
  };

  const onLoadMore = async (inView: boolean) => {
    if (inView) {
      await loadMoreCompanyTags();
    }
  };

  const optionLoadMoreTags = useMemo(
    () => ({
      key: 'linked_tags_modal_load_more_tags',
      isMulti: false,
      onClick: () => {},
      content: (
        <div className={styles.FilterTagsAutocompleteLoadMoreTags}>
          <InView
            onChange={onLoadMore}
            key="linked_tags_modal_load_more_in_view">
            <Spinner color={Colors.COLOR_ACCENT_500} />
          </InView>
        </div>
      ),
    }),
    []
  );

  const filterOptions = useMemo(() => {
    const opts: DropdownItemDataType[] = companyTags.reduce<DropdownItemDataType[]>((options, item) => {
      options.push({
        key: item.id,
        selected: state.selectedTags.some((tag) => tag.key === item.id),
        isMulti: true,
        onClick: () => {},
        content: SuggestionUtils.createSuggestionPreview(item.title, state.searchQuery, 'mark'),
        value: item.title,
      });

      return options;
    }, []);

    if (paginationInfo.hasNextPage) {
      opts.push(optionLoadMoreTags);
    }

    return opts;
  }, [companyTags, paginationInfo, state.selectedTags, state.searchQuery, optionLoadMoreTags]);

  const visibleTagTitleLength = isDesktop ? VISIBLE_TITLE_LENGTH : VISIBLE_TITLE_LENGTH_MOBILE;

  const setCroppedTags = (sliceCount: number) => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    if (state.selectedTags.length - state.moreTagsCount === sliceCount) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      if (sliceCount === 0) {
        dispatch({ type: 'setShownTags', shownTags: state.selectedTags });
        dispatch({ type: 'setMoreTagsCount', moreTagsCount: 0 });
      } else {
        const croppedTags = state.selectedTags.slice(0, sliceCount);
        const moreTagsCount = state.selectedTags.length - croppedTags.length;
        if (croppedTags.length !== state.shownTags.length || state.moreTagsCount !== moreTagsCount) {
          dispatch({ type: 'setShownTags', shownTags: croppedTags });
          dispatch({ type: 'setMoreTagsCount', moreTagsCount });
        }
      }
    });
  };

  const checkScroll = () => {
    const isNeedCropTags = !state.showAllTags;

    const commonWidth = TagFiltersUtils.getCommonWidth(state.shownTags);
    const scrollWidth = Number(rootRef.current?.scrollWidth);
    const offsetWidth = Number(rootRef.current?.offsetWidth);

    const buttonWidth = isDesktop ? DROPDOWN_DESKTOP_BUTTON_WIDTH : DROPDOWN_MOBILE_BUTTON_WIDTH;

    const toolbarWidth = dropdownInTagsRow ? buttonWidth + GRID_GAP * 2 : 0;

    const additional = toolbarWidth + (isDesktop ? DESKTOP_PADDING : MOBILE_PADDING);

    if (!isNeedCropTags) return;

    if (commonWidth + additional > offsetWidth || scrollWidth > offsetWidth) {
      const sliceCount = state.selectedTags.length - state.moreTagsCount - DUMMY_COUNT;

      setCroppedTags(Math.max(1, sliceCount));
    }

    if (offsetWidth === scrollWidth && offsetWidth - additional > commonWidth) {
      const sliceCount = state.selectedTags.length - state.moreTagsCount + DUMMY_COUNT;

      const nextCommonWidth = TagFiltersUtils.getCommonWidth(state.selectedTags.slice(0, sliceCount));
      if (nextCommonWidth + additional < offsetWidth) {
        setCroppedTags(Math.min(sliceCount, state.selectedTags.length));
      }
    }
  };

  useLayoutEffect(() => {
    checkScroll();
  }, [state.shownTags.length, state.isExpanded, isDesktop, state.moreTagsCount, state.selectedTags.length]);

  const handleNewSelectedTags = (newTags: DropdownItemDataType[]) => {
    let commonWidth = TagFiltersUtils.getCommonWidth(newTags);

    if (dropdownInTagsRow) {
      commonWidth += DROPDOWN_DESKTOP_BUTTON_WIDTH + GRID_GAP * 2;
    }

    const isWidthIncreased =
      rootRef.current?.offsetWidth && rootRef.current?.offsetWidth - commonWidth > TAG_REST_WIDTH;

    if (isWidthIncreased && state.showAllTags) {
      dispatch({ type: 'setShowAllTags', showAllTags: false });
      dispatch({ type: 'setMoreTagsCount', moreTagsCount: -DUMMY_COUNT });
    } else {
      dispatch({ type: 'setMoreTagsCount', moreTagsCount: state.moreTagsCount - DUMMY_COUNT });
    }
  };

  const expandCallback = useCallback(
    (value: boolean) => {
      dispatch({ type: 'setShowAllTags', showAllTags: value });

      if (value) {
        dispatch({ type: 'setShownTags', shownTags: state.selectedTags });
      } else {
        setCroppedTags(state.selectedTags.length - state.moreTagsCount + DUMMY_COUNT);
      }
    },
    [state.selectedTags.length, state.moreTagsCount, setCroppedTags]
  );

  const onDropdownClose = () => {
    dispatch({ type: 'setIsExpanded', isExpanded: false });
    onSearchChange('');
  };

  const onDisableTag = useCallback(
    (tag: DropdownItemDataType) => {
      const newTags = state.selectedTags.filter((selectedTag) => tag.key !== selectedTag.key);
      dispatch({ type: 'setSelectedTags', selectedTags: newTags });
      onChange?.(newTags);

      if (state.showAllTags) {
        handleNewSelectedTags(newTags);
      } else {
        dispatch({ type: 'setMoreTagsCount', moreTagsCount: 0 });
      }

      dispatch({ type: 'setShownTags', shownTags: newTags });

      TagFiltersUtils.sendEvent(EventNameAMP.COMPANY_TAG_FILTER_REMOVED, pageType);
    },
    [state.showAllTags, handleNewSelectedTags]
  );

  const onSelectTag = (tag: DropdownItemDataType) => {
    if (tag.selected) {
      onDisableTag(tag);
    } else {
      const keys = state.selectedTags.map((selectedTag) => selectedTag.key);

      if (!keys.includes(tag.key)) {
        const newTags = [...state.selectedTags, tag];
        dispatch({ type: 'setSelectedTags', selectedTags: newTags });
        onChange?.(newTags);
        dispatch({ type: 'setShownTags', shownTags: newTags });

        TagFiltersUtils.sendEvent(EventNameAMP.COMPANY_TAG_FILTER_ADDED, pageType);
      }
    }
  };

  const setIsExpanded = (value: boolean) => {
    dispatch({ type: 'setIsExpanded', isExpanded: value });
  };

  const clearAll = useCallback(() => {
    dispatch({ type: 'resetState' });
  }, []);

  useEffect(() => {
    requestCompanyTags();
  }, []);

  useEffect(() => {
    if (onChange && state.selectedTags.length === 0) {
      onChange(state.selectedTags);
    }
  }, [state.selectedTags]);

  useEffect(() => {
    if (tagIds === undefined) {
      dispatch({ type: 'setSelectedTags', selectedTags: [] });
    }
  }, [tagIds]);

  const resizeCallback = TagFiltersUtils.getResizeCallback({
    setShownTags: (tags) => dispatch({ type: 'setShownTags', shownTags: tags }),
    setMoreTagsCount: (count) => dispatch({ type: 'setMoreTagsCount', moreTagsCount: count }),
    dropdownInTagsRow,
    moreTagsCount: state.moreTagsCount,
  });

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) =>
      resizeCallback(entries, state.selectedTags, state.shownTags)
    );

    if (rootRef.current && dropdownInTagsRow) {
      resizeObserver.observe(rootRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [state.selectedTags.length]);

  return {
    isDesktop,
    visibleTagTitleLength,
    setIsExpanded,
    isExpanded: state.isExpanded,
    onDropdownClose,
    filterOptions,
    onSearchChange,
    onSelect: onSelectTag,
    menuMargin: !isDesktop ? AUTOCOMPLETE_DROPDOWN_MENU_MARGIN : undefined,
    maxVisibleOptions: AUTOCOMPLETE_MAX_VISIBLE_OPTIONS,
    onDisableTag,
    expandCallback,
    clearAll,
    shownTags: state.shownTags,
    moreTagsCount: state.moreTagsCount,
    showAllTags: state.showAllTags,
  };
};

export default useTagFiltersController;
